import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui-kit/alert-dialog';
import { AlertTriangle, Loader2 } from 'lucide-react';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import type { SerializedUser } from './users-admin-client';

interface DeleteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: SerializedUser | null;
  onConfirm: () => Promise<void>;
}

export function DeleteUserDialog({ open, onOpenChange, user, onConfirm }: DeleteUserDialogProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const { t } = useTranslation();
  const { toast } = useToast();

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Error', description: error.message || t('ADMIN_USERS_DELETEDIALOG_ERRORS_DELETEFAILED') });
    } finally {
      setIsDeleting(false);
    }
  };

  const userName =
    user?.firstName || user?.lastName
      ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
      : user?.email;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="w-[95vw] max-w-md max-h-[90dvh] overflow-y-auto p-4 sm:p-6 rounded-2xl">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex size-9 sm:size-10 shrink-0 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="size-4 sm:size-5 text-destructive" />
            </div>
            <AlertDialogTitle className="text-base sm:text-lg">
              {t('ADMIN_USERS_DELETEDIALOG_TITLE')}
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="pt-2 text-xs sm:text-sm">
            {t('ADMIN_USERS_DELETEDIALOG_DESCRIPTION', {
              name: userName as string,
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-2">
          <AlertDialogCancel disabled={isDeleting} className="w-full sm:w-auto mt-0">
            {t('ADMIN_USERS_DELETEDIALOG_ACTIONS_CANCEL')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isDeleting}
            className="w-full sm:w-auto bg-destructive text-white hover:bg-destructive/90"
          >
            {isDeleting && <Loader2 className="size-4 animate-spin mr-2" />}
            {t('ADMIN_USERS_DELETEDIALOG_ACTIONS_CONFIRM')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
